import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useChurchPlanner } from '../../hooks/useChurchPlanner';
import { supabase } from '../../services/supabase';
import { hasAnyPermission, normalizeRoleKeys, Permission } from '../../utils/roles';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerInput,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import { formatPlannerStatusLabel, normalizePlannerStatus, plannerStatusTone, resolvePlannerServiceStatus } from './plannerStatus';
import { formatSongTemplateLengthDisplay } from './songDuration';

type ScheduleSubview = 'services' | 'songs';

interface SongTemplate {
  id: string;
  title: string;
  artist?: string | null;
  key_signature?: string | null;
  length_minutes: number;
  length_seconds?: number | null;
  bpm?: number | null;
  sequence?: number | null;
  notes?: string | null;
  last_scheduled_date?: string | null;
  is_active?: boolean;
}

const FilterRow = styled.div`
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;
`;

const PlanList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const PlanItem = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  background: ${plannerColors.surfaceHover};
`;

const PlanHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
`;

const PlanTitle = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 1rem;
  font-weight: 700;
`;

const RoleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-top: 0.55rem;
  padding-top: 0.55rem;
  border-top: 1px solid ${plannerColors.border};
`;

const RoleLabel = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.64rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
`;

const RoleValue = styled.span`
  color: ${plannerColors.text};
  font-size: 0.84rem;
  font-weight: 700;
`;

const StatusPill = styled.span<{ $tone: 'ok' | 'bad' }>`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.28rem 0.62rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid
    ${({ $tone }) => ($tone === 'ok' ? 'rgba(52, 199, 89, 0.52)' : 'rgba(255, 69, 58, 0.52)')};
  color: ${({ $tone }) => ($tone === 'ok' ? '#d5f5de' : '#ffd8d5')};
  background: ${({ $tone }) => ($tone === 'ok' ? 'rgba(52, 199, 89, 0.2)' : 'rgba(255, 69, 58, 0.2)')};
`;

const CardActionsRow = styled.div`
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const OpenDetailsButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
  color: ${plannerColors.text};
  border-radius: 8px;
  padding: 0.32rem 0.58rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
`;

const AvailabilityToggleButton = styled.button<{ $mode: 'mark-available' | 'mark-not-available' }>`
  border: 1px solid
    ${({ $mode }) =>
      $mode === 'mark-available' ? 'rgba(52, 199, 89, 0.48)' : 'rgba(255, 69, 58, 0.42)'};
  background: ${({ $mode }) =>
    $mode === 'mark-available' ? 'rgba(52, 199, 89, 0.16)' : 'rgba(255, 69, 58, 0.12)'};
  color: ${({ $mode }) => ($mode === 'mark-available' ? '#d5f5de' : '#ffd8d5')};
  border-radius: 8px;
  padding: 0.32rem 0.58rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
`;

const ViewModeRow = styled.div`
  display: inline-flex;
  gap: 0.35rem;
  margin-top: 0.95rem;
  padding: 0.3rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  background: ${plannerColors.surfaceHover};
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? 'rgba(229, 211, 179, 0.7)' : 'transparent')};
  background: ${({ $active }) => ($active ? 'rgba(229, 211, 179, 0.16)' : 'transparent')};
  color: ${({ $active }) => ($active ? plannerColors.text : plannerColors.muted)};
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
`;

const SongsPanel = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const SongsControls = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const Label = styled.label`
  display: grid;
  gap: 0.35rem;
  color: ${plannerColors.text};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const SelectInput = styled.select`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.62rem 0.75rem;
  color-scheme: dark;

  &[multiple] {
    min-height: 8.5rem;
    padding: 0.4rem;
  }
`;

const SearchInput = styled.input`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.62rem 0.75rem;

  &::placeholder {
    color: ${plannerColors.muted};
  }
`;

const SearchInputSpaced = styled(SearchInput)`
  margin-top: 0.75rem;
`;

const SongsList = styled.div`
  display: grid;
  gap: 0.7rem;
`;

const SongCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  background: ${plannerColors.surfaceHover};
`;

const SongHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SongTitleButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: ${plannerColors.text};
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
`;

const ChipRow = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: 0.55rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.28rem 0.58rem;
  border: 1px solid ${plannerColors.border};
  color: ${plannerColors.text};
  background: ${plannerColors.surface};
  font-size: 0.68rem;
  font-weight: 700;
`;

const UsageText = styled.p`
  margin: 0.7rem 0 0;
  color: ${plannerColors.muted};
  font-size: 0.78rem;
  line-height: 1.5;
`;

const ActionButton = styled.button`
  border: 1px solid rgba(229, 211, 179, 0.45);
  background: rgba(229, 211, 179, 0.14);
  color: ${plannerColors.text};
  border-radius: 8px;
  padding: 0.42rem 0.7rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function overlapsServiceDate(serviceDate: string, startsAt: string, endsAt: string): boolean {
  const serviceMs = new Date(serviceDate).getTime();
  const startMs = new Date(startsAt).getTime();
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(serviceMs) || Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  return startMs <= serviceMs && endMs > serviceMs;
}

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function ensureUniqueSubsectionName(baseName: string, existingNames: string[]): string {
  const normalizedBase = baseName.trim();
  if (!normalizedBase) return baseName;

  const existing = new Set(
    existingNames
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean)
  );

  if (!existing.has(normalizedBase.toLowerCase())) {
    return normalizedBase;
  }

  let suffix = 2;
  while (existing.has(`${normalizedBase} (${suffix})`.toLowerCase())) {
    suffix += 1;
  }
  return `${normalizedBase} (${suffix})`;
}

export default function ChurchPlannerSchedulePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const rolesSource = planner.currentUserQuery.data?.roles ?? user?.user_metadata?.roles;
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [scheduleSubview, setScheduleSubview] = useState<ScheduleSubview>('services');
  const [songLibrary, setSongLibrary] = useState<SongTemplate[]>([]);
  const [songsLoading, setSongsLoading] = useState(false);
  const [songSearch, setSongSearch] = useState('');
  const [scheduledSongsSearch, setScheduledSongsSearch] = useState('');
  const [selectedSongPlanIds, setSelectedSongPlanIds] = useState<string[]>([]);
  const [songsSectionKey, setSongsSectionKey] = useState('');
  const [applyingSongId, setApplyingSongId] = useState<string | null>(null);
  const [markingUnavailablePlanId, setMarkingUnavailablePlanId] = useState<string | null>(null);

  const filteredPlans = useMemo(() => {
    const fromMs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toMs = toDate ? new Date(`${toDate}T23:59:59`).getTime() : null;
    return planner.upcomingPlans.filter((plan) => {
      const planMs = new Date(plan.service_date).getTime();
      if (Number.isNaN(planMs)) return false;
      if (fromMs && planMs < fromMs) return false;
      if (toMs && planMs > toMs) return false;
      return true;
    });
  }, [fromDate, planner.upcomingPlans, toDate]);

  const currentUserRoles = useMemo(
    () => normalizeRoleKeys(rolesSource),
    [rolesSource]
  );

  const canManageScheduleSongs =
    hasAnyPermission(rolesSource, [
      Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
      Permission.MANAGE_CHURCH_PLANNER_ROLES,
    ])
    || currentUserRoles.includes('service_planner_team_leader')
    || currentUserRoles.includes('worship_leader')
    || currentUserRoles.includes('leader');

  const selectedSongsPlans = useMemo(() => {
    const planById = new Map(filteredPlans.map((plan) => [plan.id, plan]));
    return selectedSongPlanIds
      .map((planId) => planById.get(planId) || null)
      .filter((plan): plan is typeof filteredPlans[number] => Boolean(plan));
  }, [filteredPlans, selectedSongPlanIds]);

  const commonSongsSections = useMemo(() => {
    if (!selectedSongsPlans.length) return [] as Array<{ key: string; label: string }>;

    const sectionMaps = selectedSongsPlans.map((plan) => {
      const map = new Map<string, string>();
      [...(plan.service_plan_sections ?? [])]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .forEach((section) => {
          const key = (section.name || '').trim().toLowerCase();
          if (!key || map.has(key)) return;
          map.set(key, (section.name || '').trim() || 'Section');
        });
      return map;
    });

    const [firstMap, ...remainingMaps] = sectionMaps;
    return Array.from(firstMap.entries())
      .filter(([key]) => remainingMaps.every((map) => map.has(key)))
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [selectedSongsPlans]);

  const selectedSongsSection = useMemo(
    () => commonSongsSections.find((section) => section.key === songsSectionKey) || null,
    [commonSongsSections, songsSectionKey]
  );

  useEffect(() => {
    if (scheduleSubview !== 'songs') return;
    setSelectedSongPlanIds((current) => {
      const availableIds = new Set(filteredPlans.map((plan) => plan.id));
      const nextSelected = current.filter((planId) => availableIds.has(planId));
      if (nextSelected.length === current.length && nextSelected.every((planId, index) => planId === current[index])) {
        return current;
      }
      if (nextSelected.length) return nextSelected;
      const fallbackPlanId = filteredPlans[0]?.id || '';
      return fallbackPlanId ? [fallbackPlanId] : [];
    });
  }, [filteredPlans, scheduleSubview]);

  useEffect(() => {
    if (scheduleSubview !== 'songs') return;
    setSongsSectionKey((current) => (
      commonSongsSections.some((section) => section.key === current)
        ? current
        : commonSongsSections[0]?.key || ''
    ));
  }, [commonSongsSections, scheduleSubview]);

  useEffect(() => {
    if (scheduleSubview !== 'songs') return;
    let active = true;

    const loadSongLibrary = async () => {
      setSongsLoading(true);
      const { data, error } = await supabase
        .from('service_song_templates')
        .select('id, title, artist, key_signature, length_minutes, length_seconds, bpm, sequence, notes, last_scheduled_date, is_active')
        .eq('is_active', true)
        .order('title', { ascending: true });

      if (!active) return;

      if (error) {
        window.alert(error.message || 'Unable to load songs right now.');
        setSongsLoading(false);
        return;
      }

      setSongLibrary((data as SongTemplate[] | null) ?? []);
      setSongsLoading(false);
    };

    void loadSongLibrary();

    return () => {
      active = false;
    };
  }, [scheduleSubview]);

  const filteredSongLibrary = useMemo(() => {
    const term = songSearch.trim().toLowerCase();
    if (!term) return [] as SongTemplate[];

    return songLibrary.filter((song) => {
      const title = (song.title || '').toLowerCase();
      const artist = (song.artist || '').toLowerCase();
      const keySignature = (song.key_signature || '').toLowerCase();
      const notes = (song.notes || '').toLowerCase();

      return (
        title.includes(term)
        || artist.includes(term)
        || keySignature.includes(term)
        || notes.includes(term)
      );
    });
  }, [songLibrary, songSearch]);

  const scopedScheduledSongs = useMemo(() => {
    const songById = new Map(songLibrary.map((song) => [song.id, song]));
    const entries = new Map<
      string,
      {
        song: SongTemplate;
        usages: Array<{
          id: string;
          planId: string;
          planTitle: string;
          planDate: string;
          sectionName: string;
          slotName: string;
        }>;
      }
    >();

    filteredPlans.forEach((plan) => {
      const sectionNameById = new Map((plan.service_plan_sections ?? []).map((section) => [section.id, section.name]));
      (plan.service_plan_subsections ?? []).forEach((subsection) => {
        const songId = subsection.song_template_id;
        if (!songId) return;
        const song = songById.get(songId);
        if (!song) return;

        const current = entries.get(songId) ?? {
          song,
          usages: [],
        };

        current.usages.push({
          id: `${plan.id}:${subsection.id}`,
          planId: plan.id,
          planTitle: plan.title,
          planDate: plan.service_date,
          sectionName: sectionNameById.get(subsection.section_id) || 'Section',
          slotName: subsection.name || 'Timeslot',
        });
        entries.set(songId, current);
      });
    });

    return Array.from(entries.values()).sort((a, b) => {
      const sequenceDiff = (a.song.sequence || 0) - (b.song.sequence || 0);
      if (sequenceDiff !== 0) return sequenceDiff;
      return (a.song.title || '').localeCompare(b.song.title || '', undefined, { sensitivity: 'base' });
    });
  }, [filteredPlans, songLibrary]);

  const filteredScopedScheduledSongs = useMemo(() => {
    const term = scheduledSongsSearch.trim().toLowerCase();
    if (!term) return scopedScheduledSongs;

    return scopedScheduledSongs.filter((entry) => {
      const title = (entry.song.title || '').toLowerCase();
      const artist = (entry.song.artist || '').toLowerCase();
      const keySignature = (entry.song.key_signature || '').toLowerCase();
      const notes = (entry.song.notes || '').toLowerCase();
      const usageText = entry.usages
        .map((usage) => `${usage.planTitle} ${usage.sectionName} ${usage.slotName}`.toLowerCase())
        .join(' ');

      return (
        title.includes(term)
        || artist.includes(term)
        || keySignature.includes(term)
        || notes.includes(term)
        || usageText.includes(term)
      );
    });
  }, [scopedScheduledSongs, scheduledSongsSearch]);

  const availabilityContextByPlanId = useMemo(() => {
    const map = new Map<string, { serviceUnavailable: number; calendarUnavailable: number }>();

    planner.plans.forEach((plan) => {
      const serviceUnavailableUsers = new Set<string>();
      (planner.availability ?? []).forEach((entry) => {
        if (entry.service_plan_id !== plan.id || !entry.user_id) return;
        if ((entry.availability_status || '').trim().toLowerCase() === 'unavailable') {
          serviceUnavailableUsers.add(entry.user_id);
        }
      });

      const calendarUnavailableUsers = new Set<string>();
      (planner.memberUnavailability ?? []).forEach((entry) => {
        if (!entry.user_id) return;
        if (overlapsServiceDate(plan.service_date, entry.starts_at, entry.ends_at)) {
          calendarUnavailableUsers.add(entry.user_id);
        }
      });

      map.set(plan.id, {
        serviceUnavailable: serviceUnavailableUsers.size,
        calendarUnavailable: calendarUnavailableUsers.size,
      });
    });

    return map;
  }, [planner.availability, planner.memberUnavailability, planner.plans]);

  const userCalendarBlocks = useMemo(
    () => (planner.memberUnavailability ?? []).filter((entry) => entry.user_id === user?.id),
    [planner.memberUnavailability, user?.id]
  );

  const userServiceAvailabilityByPlanId = useMemo(() => {
    const map = new Map<string, string>();
    if (!user?.id) return map;

    (planner.availability ?? []).forEach((entry) => {
      if (entry.user_id !== user.id || !entry.service_plan_id) return;
      map.set(entry.service_plan_id, normalizePlannerStatus(entry.availability_status));
    });

    return map;
  }, [planner.availability, user?.id]);

  const getPlanAssignmentForCurrentUser = (planId: string) => {
    const plan = planner.plans.find((entry) => entry.id === planId);
    if (!plan || !user?.id) return null;
    return (plan.service_plan_assignments ?? []).find((entry) => entry.user_id === user.id) ?? null;
  };

  const getPlanAssignmentsForCurrentUser = (planId: string) => {
    const plan = planner.plans.find((entry) => entry.id === planId);
    if (!plan || !user?.id) return [];
    return (plan.service_plan_assignments ?? []).filter((entry) => entry.user_id === user.id && entry.id);
  };

  const handleMarkUnavailableForPlan = async (planId: string) => {
    if (!user?.id) {
      window.alert('Please sign in before setting availability.');
      return;
    }

    const plan = planner.plans.find((entry) => entry.id === planId);
    if (!plan) return;

    setMarkingUnavailablePlanId(planId);
    try {
      await planner.upsertAvailability({
        service_plan_id: planId,
        user_id: user.id,
        availability_status: 'unavailable',
        notes: null,
        updated_by: user.id,
      });

      const assignments = getPlanAssignmentsForCurrentUser(planId).filter(
        (assignment) => normalizePlannerStatus(assignment.status) !== 'unavailable'
      );
      if (assignments.length) {
        await Promise.all(assignments.map((assignment) => planner.updateAssignment({
          assignmentId: assignment.id,
          updates: { status: 'declined' },
        })));
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to mark this service as not available.');
    } finally {
      setMarkingUnavailablePlanId(null);
    }
  };

  const handleMarkAvailableForPlan = async (planId: string) => {
    if (!user?.id) {
      window.alert('Please sign in before setting availability.');
      return;
    }

    const plan = planner.plans.find((entry) => entry.id === planId);
    if (!plan) return;

    setMarkingUnavailablePlanId(planId);
    try {
      await planner.upsertAvailability({
        service_plan_id: planId,
        user_id: user.id,
        availability_status: 'available',
        notes: null,
        updated_by: user.id,
      });

      const assignments = getPlanAssignmentsForCurrentUser(planId).filter((assignment) => {
        const normalizedStatus = normalizePlannerStatus(assignment.status);
        return normalizedStatus !== 'confirmed';
      });
      if (assignments.length) {
        await Promise.all(assignments.map((assignment) => planner.updateAssignment({
          assignmentId: assignment.id,
          updates: { status: 'confirmed' },
        })));
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to mark this service as available.');
    } finally {
      setMarkingUnavailablePlanId(null);
    }
  };

  const handleApplySongTemplate = async (song: SongTemplate) => {
    if (!selectedSongsPlans.length || !selectedSongsSection) {
      window.alert('Select one or more services and a shared section first.');
      return;
    }

    const planTargets = selectedSongsPlans.map((plan) => {
      const section = (plan.service_plan_sections ?? []).find(
        (entry) => (entry.name || '').trim().toLowerCase() === selectedSongsSection.key
      ) || null;

      return {
        plan,
        section,
      };
    });

    if (planTargets.some((entry) => !entry.section)) {
      window.alert('One or more selected services no longer has that shared section.');
      return;
    }

    setApplyingSongId(song.id);
    const results = await Promise.all(
      planTargets.map(async ({ plan, section }) => {
        const currentSectionSubsections = (plan.service_plan_subsections ?? [])
          .filter((subsection) => subsection.section_id === section!.id)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const uniqueName = ensureUniqueSubsectionName(
          song.title,
          currentSectionSubsections.map((subsection) => subsection.name || '')
        );
        const maxSortOrder = currentSectionSubsections.length
          ? (currentSectionSubsections[currentSectionSubsections.length - 1].sort_order || 0)
          : -1;

        return supabase
          .from('service_plan_subsections')
          .insert({
            service_plan_id: plan.id,
            section_id: section!.id,
            song_template_id: song.id,
            name: uniqueName,
            description: song.notes || null,
            length_minutes: song.length_minutes,
            length_display: formatSongTemplateLengthDisplay(song),
            sort_order: maxSortOrder + 1,
          });
      })
    );
    setApplyingSongId(null);

    const firstError = results.find((result) => result.error)?.error;
    if (firstError) {
      window.alert(firstError.message || 'Unable to add song to the selected services.');
      return;
    }

    await planner.plansQuery.refetch();
  };

  return (
    <PlannerPage>
      <PlannerCard>
        <PlannerKicker>Calendar</PlannerKicker>
        <PlannerHeading>Schedules</PlannerHeading>
        <PlannerSubtle>All service plans ordered by date.</PlannerSubtle>
        <FilterRow>
          <PlannerInput
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            aria-label="From date"
          />
          <PlannerInput
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            aria-label="To date"
          />
        </FilterRow>
        <ViewModeRow>
          <ViewModeButton
            type="button"
            $active={scheduleSubview === 'services'}
            onClick={() => setScheduleSubview('services')}
          >
            Services
          </ViewModeButton>
          <ViewModeButton
            type="button"
            $active={scheduleSubview === 'songs'}
            onClick={() => setScheduleSubview('songs')}
          >
            Songs
          </ViewModeButton>
        </ViewModeRow>
        {planner.isError ? <PlannerError>Unable to load schedule right now.</PlannerError> : null}
      </PlannerCard>

      {scheduleSubview === 'services' ? (
        <PlannerCard>
          {planner.isLoading ? (
            <PlannerSubtle>Loading plans…</PlannerSubtle>
          ) : filteredPlans.length === 0 ? (
            <PlannerSubtle>No plans found for this date range.</PlannerSubtle>
          ) : (
            <PlanList>
              {filteredPlans.map((plan) => (
                <PlanItem key={plan.id}>
                  {(() => {
                    const assignment = getPlanAssignmentForCurrentUser(plan.id);
                    const serviceAvailability = userServiceAvailabilityByPlanId.get(plan.id) || 'available';
                    const hasCalendarBlock = userCalendarBlocks.some((entry) =>
                      overlapsServiceDate(plan.service_date, entry.starts_at, entry.ends_at)
                    );
                    const resolvedStatus = resolvePlannerServiceStatus({
                      assignmentStatus: assignment?.status,
                      availabilityStatus: serviceAvailability,
                      hasCalendarBlock,
                    });
                    const assignmentConfirmed = resolvedStatus === 'confirmed';
                    const unavailable = resolvedStatus === 'unavailable';
                    const statusLabel = formatPlannerStatusLabel(resolvedStatus);
                    const statusTone = plannerStatusTone(resolvedStatus);
                    const role = firstRelation(assignment?.service_plan_roles);
                    const roleName = role?.name || 'Unassigned role';
                    const isPastService = new Date(plan.service_date).getTime() < Date.now();
                    const isSaving = markingUnavailablePlanId === plan.id;
                    const notAvailableDisabled = isPastService || isSaving || planner.isMutating;
                    const actionMode: 'mark-available' | 'mark-not-available' = unavailable
                      ? 'mark-available'
                      : 'mark-not-available';

                    return (
                      <>
                        <PlanHeaderRow>
                          <PlannerSubtle>{formatDateTime(plan.service_date)}</PlannerSubtle>
                          <StatusPill $tone={statusTone}>{statusLabel}</StatusPill>
                        </PlanHeaderRow>
                        <PlanTitle>{plan.title}</PlanTitle>
                        <PlannerSubtle>{plan.location || 'Location not set'}</PlannerSubtle>
                        <RoleRow>
                          <div>
                            <RoleLabel>Your Role</RoleLabel>
                            <RoleValue>{roleName}</RoleValue>
                          </div>
                          <PlannerSubtle>
                            Assignments: {plan.service_plan_assignments?.length ?? 0}
                          </PlannerSubtle>
                        </RoleRow>
                        <PlannerSubtle>
                          Unavailable: {availabilityContextByPlanId.get(plan.id)?.calendarUnavailable ?? 0} calendar
                          {' • '}
                          {availabilityContextByPlanId.get(plan.id)?.serviceUnavailable ?? 0} per-service
                        </PlannerSubtle>
                        <CardActionsRow>
                          <OpenDetailsButton onClick={() => navigate(`/church-planner/services/${plan.id}`)}>
                            Open Service
                          </OpenDetailsButton>
                          {!assignmentConfirmed ? (
                            <AvailabilityToggleButton
                              $mode={actionMode}
                              disabled={notAvailableDisabled}
                              onClick={() => {
                                if (unavailable) {
                                  if (!window.confirm('Mark yourself available for this service?')) return;
                                  void handleMarkAvailableForPlan(plan.id);
                                  return;
                                }

                                if (!window.confirm('Mark yourself not available for this service?')) return;
                                void handleMarkUnavailableForPlan(plan.id);
                              }}
                            >
                              {isSaving
                                ? 'Saving...'
                                : isPastService
                                  ? 'Past Service'
                                  : unavailable
                                    ? '✓ MARK AVAILABLE'
                                    : 'MARK NOT AVAILABLE'}
                            </AvailabilityToggleButton>
                          ) : null}
                        </CardActionsRow>
                      </>
                    );
                  })()}
                </PlanItem>
              ))}
            </PlanList>
          )}
        </PlannerCard>
      ) : (
        <SongsPanel>
          {canManageScheduleSongs ? (
            <PlannerCard>
              <PlannerHeading style={{ marginBottom: '0.4rem' }}>Add Songs To Services</PlannerHeading>
              <PlannerSubtle>
                Search the reusable song library, then add a song to a service inside the current schedule range.
              </PlannerSubtle>
              <SongsControls>
                <Label>
                  Services
                  <SelectInput
                    multiple
                    size={Math.min(6, Math.max(filteredPlans.length, 2))}
                    value={selectedSongPlanIds}
                    onChange={(event) => setSelectedSongPlanIds(
                      Array.from(event.target.selectedOptions, (option) => option.value)
                    )}
                  >
                    {filteredPlans.length ? null : <option value="">No services in range</option>}
                    {filteredPlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {`${plan.title} • ${formatDateTime(plan.service_date)}`}
                      </option>
                    ))}
                  </SelectInput>
                </Label>
                <Label>
                  Section
                  <SelectInput value={songsSectionKey} onChange={(event) => setSongsSectionKey(event.target.value)}>
                    {commonSongsSections.length ? null : <option value="">No shared sections available</option>}
                    {commonSongsSections.map((section) => (
                      <option key={section.key} value={section.key}>
                        {section.label}
                      </option>
                    ))}
                  </SelectInput>
                </Label>
                <Label>
                  Search Song Library
                  <SearchInput
                    value={songSearch}
                    onChange={(event) => setSongSearch(event.target.value)}
                    placeholder="Type title, artist, key, or notes"
                  />
                </Label>
              </SongsControls>
              {!filteredPlans.length ? <PlannerSubtle>No services are scheduled in this date range.</PlannerSubtle> : null}
              {filteredPlans.length && !commonSongsSections.length ? (
                <PlannerSubtle>Select services that share the same section name.</PlannerSubtle>
              ) : null}
              {!songSearch.trim() ? <PlannerSubtle>Start typing to search the reusable song library.</PlannerSubtle> : null}
              {songsLoading ? <PlannerSubtle>Loading songs...</PlannerSubtle> : null}
              {!songsLoading && !!songSearch.trim() && filteredSongLibrary.length === 0 ? (
                <PlannerSubtle>No songs match your search.</PlannerSubtle>
              ) : null}
              {!!songSearch.trim() && filteredSongLibrary.length > 0 ? (
                <SongsList>
                  {filteredSongLibrary.map((song) => (
                    <SongCard key={song.id}>
                      <SongHeaderRow>
                        <div>
                          <SongTitleButton type="button" onClick={() => navigate(`/church-planner/songs/${song.id}`)}>
                            {song.title}
                          </SongTitleButton>
                          <PlannerSubtle>{song.artist || 'Unknown artist'}</PlannerSubtle>
                        </div>
                        <ActionButton
                          type="button"
                          onClick={() => void handleApplySongTemplate(song)}
                          disabled={applyingSongId === song.id || !selectedSongPlanIds.length || !selectedSongsSection}
                        >
                          {applyingSongId === song.id ? 'Adding...' : 'Add Slot'}
                        </ActionButton>
                      </SongHeaderRow>
                      <ChipRow>
                        <Chip>{formatSongTemplateLengthDisplay(song) || 'No duration'}</Chip>
                        <Chip>{song.key_signature || 'No key'}</Chip>
                        <Chip>{song.bpm || '--'} BPM</Chip>
                      </ChipRow>
                    </SongCard>
                  ))}
                </SongsList>
              ) : null}
            </PlannerCard>
          ) : null}

          <PlannerCard>
            <PlannerHeading style={{ marginBottom: '0.4rem' }}>Scheduled Songs</PlannerHeading>
            <PlannerSubtle>
              Only songs already scheduled inside the current date range are shown here.
            </PlannerSubtle>
            <SearchInputSpaced
              value={scheduledSongsSearch}
              onChange={(event) => setScheduledSongsSearch(event.target.value)}
              placeholder="Search scheduled songs"
              aria-label="Search scheduled songs"
            />
            {planner.isLoading || songsLoading ? <PlannerSubtle>Loading scheduled songs...</PlannerSubtle> : null}
            {!planner.isLoading && filteredPlans.length === 0 ? (
              <PlannerSubtle>No services found for this date range.</PlannerSubtle>
            ) : null}
            {!planner.isLoading && filteredPlans.length > 0 && scopedScheduledSongs.length === 0 ? (
              <PlannerSubtle>No songs have been scheduled in this date range yet.</PlannerSubtle>
            ) : null}
            {!planner.isLoading && scopedScheduledSongs.length > 0 && filteredScopedScheduledSongs.length === 0 ? (
              <PlannerSubtle>No scheduled songs match your search.</PlannerSubtle>
            ) : null}
            {filteredScopedScheduledSongs.length > 0 ? (
              <SongsList>
                {filteredScopedScheduledSongs.map((entry) => (
                  <SongCard key={entry.song.id}>
                    <SongHeaderRow>
                      <div>
                        <SongTitleButton type="button" onClick={() => navigate(`/church-planner/songs/${entry.song.id}`)}>
                          {entry.song.title}
                        </SongTitleButton>
                        <PlannerSubtle>
                          {entry.song.artist || 'Unknown artist'} • {entry.usages.length} scheduled slot{entry.usages.length === 1 ? '' : 's'}
                        </PlannerSubtle>
                      </div>
                      <OpenDetailsButton type="button" onClick={() => navigate(`/church-planner/songs/${entry.song.id}`)}>
                        View Song
                      </OpenDetailsButton>
                    </SongHeaderRow>
                    <ChipRow>
                      <Chip>{formatSongTemplateLengthDisplay(entry.song) || 'No duration'}</Chip>
                      <Chip>{entry.song.key_signature || 'No key'}</Chip>
                      <Chip>{entry.song.bpm || '--'} BPM</Chip>
                    </ChipRow>
                    <UsageText>
                      {entry.usages
                        .slice(0, 4)
                        .map((usage) => `${formatDateTime(usage.planDate)} • ${usage.planTitle} • ${usage.sectionName}: ${usage.slotName}`)
                        .join(' | ')}
                      {entry.usages.length > 4 ? ` | +${entry.usages.length - 4} more` : ''}
                    </UsageText>
                  </SongCard>
                ))}
              </SongsList>
            ) : null}
          </PlannerCard>
        </SongsPanel>
      )}
    </PlannerPage>
  );
}
